import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Switch, FormControlLabel,
  MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import api from '../../api/client';

const LINE_TYPES = ['911', 'Admin', 'Ringdown', 'Other'];

interface Extension {
  id?: number;
  extension: string;
  label: string;
  line_type: string;
}

interface OperatorPosition {
  id: number;
  name: string;
  is_active: boolean;
  sort_order: number;
  extensions: Extension[];
}

export default function OperatorPositionsPage() {
  const [positions, setPositions] = useState<OperatorPosition[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<OperatorPosition | null>(null);
  const [form, setForm] = useState({ name: '', is_active: true, sort_order: 0 });
  const [extensions, setExtensions] = useState<Extension[]>([]);

  const load = () => api.get('/operator-positions').then((r) => setPositions(r.data));
  useEffect(() => { load(); }, []);

  const handleOpen = (pos?: OperatorPosition) => {
    if (pos) {
      setEditing(pos);
      setForm({ name: pos.name, is_active: pos.is_active, sort_order: pos.sort_order });
      setExtensions(pos.extensions.map((e) => ({ extension: e.extension, label: e.label, line_type: e.line_type || 'Admin' })));
    } else {
      setEditing(null);
      setForm({ name: '', is_active: true, sort_order: 0 });
      setExtensions([]);
    }
    setOpen(true);
  };

  const handleSave = async () => {
    const payload = { ...form, extensions };
    if (editing) {
      await api.patch(`/operator-positions/${editing.id}`, payload);
    } else {
      await api.post('/operator-positions', payload);
    }
    setOpen(false);
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this operator position?')) return;
    await api.delete(`/operator-positions/${id}`);
    load();
  };

  const addExtension = () => {
    setExtensions([...extensions, { extension: '', label: '', line_type: 'Admin' }]);
  };

  const updateExtension = (idx: number, field: keyof Extension, value: string) => {
    const updated = [...extensions];
    updated[idx] = { ...updated[idx], [field]: value };
    setExtensions(updated);
  };

  const removeExtension = (idx: number) => {
    setExtensions(extensions.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Operator Positions</Typography>
        <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>
          Add Position
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define dispatch operator positions and their associated PBX extensions.
        Each position can have multiple extensions (e.g., admin line, 911 line).
        Dispatchers select their position at login to receive auto-created calls from inbound phone calls.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Position Name</TableCell>
              <TableCell>Extensions</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sort Order</TableCell>
              <TableCell width={100}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map((pos) => (
              <TableRow key={pos.id}>
                <TableCell sx={{ fontWeight: 600 }}>{pos.name}</TableCell>
                <TableCell>
                  {pos.extensions.map((ext, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`${ext.extension}${ext.label ? ' - ' + ext.label : ''} [${(ext as any).line_type || 'Admin'}]`}
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={pos.is_active ? 'Active' : 'Inactive'}
                    color={pos.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>{pos.sort_order}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(pos)}><Edit /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(pos.id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {positions.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No operator positions configured. Add positions to enable PBX integration.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Operator Position' : 'New Operator Position'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Position Name" margin="normal"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Position 1, Dispatch Console A"
          />
          <TextField
            fullWidth label="Sort Order" type="number" margin="normal"
            value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
          />
          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />}
            label="Active"
            sx={{ mt: 1 }}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
            Extensions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Add the PBX extensions mapped to this operator position. When a call is answered on any of these extensions, a new incident will be auto-created for the dispatcher at this position.
          </Typography>

          {extensions.map((ext, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <TextField
                size="small" label="Extension" value={ext.extension}
                onChange={(e) => updateExtension(idx, 'extension', e.target.value)}
                sx={{ width: 120 }}
                placeholder="e.g., 201"
              />
              <TextField
                size="small" label="Label (optional)" value={ext.label}
                onChange={(e) => updateExtension(idx, 'label', e.target.value)}
                sx={{ flex: 1 }}
                placeholder="e.g., Main Admin, 911 Trunk"
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Line Type</InputLabel>
                <Select
                  value={ext.line_type || 'Admin'}
                  label="Line Type"
                  onChange={(e) => updateExtension(idx, 'line_type', e.target.value)}
                >
                  {LINE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <IconButton size="small" color="error" onClick={() => removeExtension(idx)}>
                <Delete />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<Add />} onClick={addExtension} sx={{ mt: 1 }}>
            Add Extension
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
